import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { Container, Kicker, Mark } from './primitives';
import { FUNZIONALITA } from '@/content/funzionalita';
import { cn } from '@/lib/utils';

interface FeatureGridProps {
  index?: string;
  /** Slug da non mostrare (la pagina in cui ci si trova). */
  escludi?: string;
  compact?: boolean;
}

export function FeatureGrid({ index = '02', escludi, compact = false }: FeatureGridProps) {
  const items = FUNZIONALITA.filter((f) => f.slug !== escludi);

  return (
    <section id="funzionalita" className="relative border-t border-border py-28 md:py-36">
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl"
        >
          <Kicker index={index} label={compact ? 'Le altre funzionalità' : 'Cosa fa BOSS'} />
          <h2 className="boss-display mt-5 text-balance text-[44px] md:text-[72px]">
            {compact ? (
              <>
                Un solo sistema, <Mark>tutta l’impresa.</Mark>
              </>
            ) : (
              <>
                La commessa, <Mark>dall’inizio alla fine.</Mark>
              </>
            )}
          </h2>
          {!compact && (
            <p className="mt-5 max-w-xl text-pretty text-[16px] leading-[1.55] text-fg-2">
              Project management della commessa, contabilità analitica, persone e paghe, acquisti, gare e
              direzione: un unico modello di dati, dove ogni informazione si inserisce una volta sola.
            </p>
          )}
        </motion.div>

        <div className="mt-14 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-border bg-border md:grid-cols-2 lg:grid-cols-3">
          {items.map((f, i) => (
            <motion.div
              key={f.slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
              // Con tutte e 7 le funzionalità commessa e paghe occupano due colonne, così la griglia resta piena.
              className={cn(
                'bg-background',
                !escludi && f.slug === 'gestione-commessa' && 'md:col-span-2',
                !escludi && f.slug === 'presenze-buste-paga' && 'lg:col-span-2',
              )}
            >
              <Link
                to={`/funzionalita/${f.slug}`}
                className="group flex h-full flex-col p-7 transition-colors hover:bg-card"
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-primary">{f.code}</div>
                <h3 className="boss-h1 mt-4 text-balance text-[24px] md:text-[26px]">{f.name}</h3>
                <p className="mt-3 text-pretty text-[14px] leading-[1.6] text-fg-2">{f.teaser}</p>
                <span className="mt-auto pt-6 text-[13px] text-fg-2 transition-colors group-hover:text-primary">
                  Scopri di più <span aria-hidden className="inline-block transition-transform group-hover:translate-x-1">→</span>
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </Container>
    </section>
  );
}
